import { EcoaContent } from "@components/common/EconContent";
import { EcoaAuthenticateCodeFlax } from "@maven-rest/cdms/ecoa/EcoaAuthenticateCode.flax";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useRequest } from "@coralblack/flax";
import { Footer } from "@layouts/Footer";
import { MavenService, TwoFactorAuthType } from "maven-lib/dist/dto/entity";
import { sessionSlice } from "maven-lib/dist/states";
import { ecoaAuthenticateSmsApi } from "maven-lib/dist/states/apis/ecoaAuthenticateCode";
import { subjectSmsApi } from "maven-lib/dist/states/apis/subjectSms";
import { HbButton } from "@maven-surface/components/base/HbButton";
import { HbCodeInput } from "@maven-surface/components/base/HbCodeInput";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";
import { HbRequestButton } from "@maven-surface/components/base/HbRequestButton";
import { useRoute } from "@maven-surface/hooks/useRoute";
import { CDMS_STUDY_SUBJECT_CODE } from "@maven-msg/code/cdms";
import { CDMS_STUDY_STATUS_CODE } from "@maven-msg/code/cdms/studyStatus";
import { ECOA_ACCESS_CODE_CODE } from "@maven-msg/code/ecoaAccessCode";
import { ECOA_SUBJECT_ACCESS_CODE_LANG } from "@maven-msg/lang/ecoa";
import { ECOA_ACCESS_CODE_LANG } from "@maven-msg/lang/ecoaAccessCode";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

interface IdentityVerificationBySmsProps {}

export function IdentityVerificationBySms(props: IdentityVerificationBySmsProps) {
  const { query } = useRoute();
  const [smsCode, setSmsCode] = useState<string>("");
  const [contact, setContact] = useState<string>("");
  const [isSentSms, setIsSentSms] = useState<boolean>(false);
  const [isAliveSms, setIsAliveSms] = useState<boolean>(false);
  const encryptedAccessCode = String(query?.encryptedAccessCode);

  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const codeLength = 6;

  const dispatch = useDispatch();

  const { request: requestToSendSms } = useRequest(
    { ...subjectSmsApi.sendEcoaAuthenticateSms },
    {
      success: (data) => {
        setIsAliveSms(false);
        setContact(data?.subjectContact);
        setIsSentSms(!!data?.subjectContact);
        MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SEND, { id: data?.subjectSmsId || "null" });
      },
      error: (error) => {
        if (error.code === ECOA_ACCESS_CODE_CODE.ECOA_AUTH_SMS_ALREADY_RESEND.code) {
          setIsAliveSms(true);
          return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_ALREADY_RESEND_TEXT);
        } else if (error.code === CDMS_STUDY_SUBJECT_CODE.CDMS_SUBJECT_CONTACT_NOT_AVAILABLE.code) {
          return MvnMessage(ECOA_SUBJECT_ACCESS_CODE_LANG.ECOA_SUBJECT_CONTACT_INVALID);
        } else {
          return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SEND_FAIL, { message: error?.message || "An error has occurred." });
        }
      },
    }
  );

  const { request: requestSms } = useRequest(
    { ...ecoaAuthenticateSmsApi.getAliveEcoaAuthenticateSms },
    {
      success: (resp) => {
        if (!resp.isAliveAuthenticateCodeSms) {
          requestToSendSms({
            data: {
              encryptedAccessCode: encryptedAccessCode,
              service: MavenService.MavenECoa,
            },
          });
        }
        setIsAliveSms(resp.isAliveAuthenticateCodeSms || false);
      },
      error: (error) => `An error has occurred. (${error.message})`,
    }
  );

  function handleCodeChange(code: string) {
    setSmsCode(code);
  }

  function handleCodeFilledUp() {
    const inputs = nextButtonRef?.current?.parentElement?.getElementsByTagName("button");
    if (!inputs?.length) return;
    inputs[0]?.focus();
  }

  const resendSms = () => {
    requestToSendSms({
      data: {
        encryptedAccessCode: encryptedAccessCode,
        service: MavenService.MavenECoa,
      },
    });
  };

  useEffect(() => {
    setIsAliveSms(false);
    requestSms({ query: { encryptedAccessCode } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <EcoaContent className="app-ecoa-page-home-code-verification" verticalAlign="CENTER">
        <HbParagraph
          align="CENTER"
          margin={2}
          color="GRAY"
          text={
            isAliveSms
              ? MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SEND_ALREADY)
              : contact
              ? MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_TEXT_LABEL, { contact: contact.slice(-4) })
              : isSentSms
              ? MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SENDING)
              : MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_SEND_FAIL_TEXT_LABEL)
          }
        />
        <HbDivider space={1} />
        <HbParagraph bold={true} align="CENTER">
          {MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_PLACEHOLDER)}
        </HbParagraph>
        <HbCodeInput length={codeLength} code={smsCode} onValueChange={handleCodeChange} onInputCompleted={handleCodeFilledUp} />
        <HbButton
          text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_REQUEST_RESEND_TEXT)}
          color={"TRANSPARENT"}
          useTextUnderline
          onClick={resendSms}
        ></HbButton>

        <HbDivider space={1} />
        <HbButton
          text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_INPUT_RESET_TEXT)}
          color="PRIMARY"
          disabled={!smsCode?.trim().length}
          onClick={() => {
            setSmsCode("");
          }}
        ></HbButton>
      </EcoaContent>
      <Footer>
        <HbRequestButton
          elementRef={nextButtonRef}
          text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)}
          iconRight="ChromeBackMirrored"
          api={{
            ...EcoaAuthenticateCodeFlax.verifyEcoaAuthenticateCode({
              encryptedAccessCode,
              twoFactorAuthType: TwoFactorAuthType.Sms,
              twoFactorAuthValue: smsCode,
            }),
          }}
          delegate={{
            success: (resp) => {
              dispatch(sessionSlice.actions.signIn({ ...resp, redirectTo: "/pat/visits" }));
            },
            error: (error) => {
              if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
                dispatch(sessionSlice.actions.signOut());
              }

              return MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_SMS_FAIL, { message: error }, [
                ECOA_ACCESS_CODE_CODE.ECOA_SMS_CODE_INVALID,
                ECOA_ACCESS_CODE_CODE.ECOA_SMS_CODE_RATE_LIMIT,
              ]);
            },
          }}
        />
      </Footer>
    </>
  );
}
